// ROUTES
import { getCarJobsPath, getJobListTemplatesPath } from "~/features/jobs/routes";
import { getIndexPath as getAssemblyPath } from "~/features/assemblies-parts/assemblies/routes";
import { getIndexPath as getAssemblyTemplatesPath } from "~/features/templates/assembly-templates/routes";
import { getIndexPath as getBOPTrackerPath } from "~/features/analysis/bop-tracker/routes";
import { getIndexPath as getCalculationsPath } from "~/features/templates/calculations/routes";
import { getIndexPath as getCalendarPath } from "~/features/calendar/routes";
import { getIndexPath as getCarDataServicePath } from "~/features/system/agents/car-data-service/routes";
import { getIndexPath as getChassisPath } from "~/features/admin/chassis/routes";
import { getIndexPath as getContestantsPath } from "~/features/admin/contestants/routes";
import { getIndexPath as getDataOverviewPath } from "~/features/system/server-data/data-overview/setup-sheets/routes";
import { getIndexPath as getDepartmentsPath } from "~/features/admin/departments/routes";
import { getIndexPath as getEventTyresPath } from "~/features/tyres/tyresheet/event-tyres/routes";
import { getIndexPath as getWarehouseTyresPath } from "~/features/tyres/warehouse/routes";
import { getIndexPath as getIssueManagerPath } from "~/features/issue-manager/routes";
import { getIndexPath as getKPIOverviewPath } from "~/features/analysis/kpi-overview/routes";
import { getIndexPath as getLapSequencesPath } from "~/features/templates/lap-sequence/routes";
import { getIndexPath as getLocationsPath } from "~/features/admin/locations/routes";
import { getIndexPath as getManufacturersPath } from "~/features/admin/manufacturers/routes";
import { getIndexPath as getMultipleEventsControllerPath } from "~/features/system/agents/timing-service/routes";
import { getIndexPath as getMyJobsPath } from "~/features/jobs/my-jobs/routes";
import { getIndexPath as getOfflineStatusPath } from "~/features/system/server-data/offline-status/routes";
import { getIndexPath as getPackagesPath } from "~/features/templates/packages/routes";
import { getIndexPath as getPartsPath } from "~/features/assemblies-parts/parts/routes";
import { getIndexPath as getPersonsPath } from "~/features/admin/persons/routes";
import { getIndexPath as getPointsSystemPath } from "~/features/admin/points-system/routes";
import { getIndexPath as getProfilePath } from "~/features/profile/routes";
import { getIndexPath as getProtocolDashboardPath } from "~/features/protocol/dashboard/routes";
import { getIndexPath as getProtocolReportPath } from "~/features/protocol/report/routes";
import { getIndexPath as getRaceSeriesListPath } from "~/features/admin/event-manager/race-series/routes";
import { getIndexPath as getRunComparePath } from "~/features/protocol/run-compare/routes";
import { getIndexPath as getRunSheetsPath } from "~/features/protocol/runsheets/routes";
import { getIndexPath as getRunSheetStylesPath } from "~/features/templates/runsheet-styles/routes";
import { getIndexPath as getServerSettingsPath } from "~/features/admin/server-settings/routes";
import { getIndexPath as getSessionDebriefPath } from "~/features/templates/session-debrief/routes";
import { getIndexPath as getSetupOverviewPath } from "~/features/analysis/car-overview/routes";
import { getIndexPath as getSetupSheetsMainViewPath } from "~/features/protocol/setupsheets/routes";
import { getIndexPath as getSetupSheetStylesPath } from "~/features/templates/setup-sheet-styles/routes";
import { getIndexPath as getSimulationPath } from "~/features/simulation/routes";
import { getIndexPath as getSpecOverviewPath } from "~/features/specifications/routes";
import { getIndexPath as getSsoProviderPath } from "~/features/system/server-data/sso/routes";
import { getIndexPath as getTagsPath } from "~/features/admin/tags/routes";
import { getIndexPath as getTeamsPath } from "~/features/admin/teams/routes";
import { getIndexPath as getTimingWeatherServicePath } from "~/features/system/agents/timing-weather/routes";
import { getIndexPath as getTracksPath } from "~/features/admin/tracks/routes";
import { getIndexPath as getTyrePressureCalculationPath } from "~/features/tyres/tyrepressure-calculation/routes";
import { getIndexPath as getUserEnumsPath } from "~/features/admin/user-enums/routes";
import { getIndexPath as getWeatherPath } from "~/features/weather/routes";
import { getIndexPath as getWeatherStationPath } from "~/features/system/agents/weather-station/routes";
import { getIndexPath as getPartsInEventsPath } from "~/features/assemblies-parts/parts-in-events/routes";
import { getIndexPath as getChatPath } from "~/features/chat/routes";

import {
  getTimeSheetExportPath,
  getTimeTrackerAdminPath,
  getIndexPath as getTimeTrackerPath,
} from "~/features/timetracker/routes";

// OTHER IMPORTS
import {
  faCalendarAlt,
  faCalendarCheck,
  faClipboard,
  faFileLines,
  faObjectUngroup,
  type IconDefinition,
} from "@fortawesome/free-regular-svg-icons";
import {
  faBook,
  faChartLine,
  faCloudSun,
  faCoins,
  faHome,
  faLaptop,
  faNetworkWired,
  faSlidersH,
  faTasks,
  faTruckMonster,
  faUser,
  faWalkieTalkie,
  faComments,
  faWarning,
  faWrench,
} from "@fortawesome/free-solid-svg-icons";
import config from "~/config";
import isDev from "~/features/common/utils/isDev";
import { faRaceHelmet } from "~/features/common/assets/customIcons/faRaceHelmet";
import { useAuth } from "~/features/common/context/AuthContext";
import { resetConfigValues } from "~/features/common/utils/resetConfigValues";
import { useThemeContext } from "~/features/common/utils/services/HandleThemeToggling";
import { EnumPersonRole, useGetPersonInfoForProfilePageQuery } from "~/generated/graphql";
import { queryClient } from "~/graphql/queryClient";
import { urqlReinitialize } from "~/graphql/urqlClient";
import { clearOneRacehubDb, exportDbToJson } from "~/indexDB";
import { getUserID, hasRequiredRoles } from "../../../utils/loginChecks";

type RequiredFeature =
  | "dyno"
  | "moveServer"
  | "readOnlyAfterLicenseExpiry"
  | "sevenPost"
  | "simulation"
  | "teams"
  | "tyreSetStatistics"
  | "lightVersion";

export const allRolesExceptDriver = [
  EnumPersonRole.ADMIN,
  EnumPersonRole.CAR_ENGINEER,
  EnumPersonRole.CHIEF_ENGINEER,
  EnumPersonRole.FAULT_ADMIN,
  EnumPersonRole.MANAGEMENT,
  EnumPersonRole.MECHANIC,
  EnumPersonRole.READ_ONLY,
  EnumPersonRole.SETUP_REVIEWER,
  EnumPersonRole.TYRE_ENGINEER,
  EnumPersonRole.WORKSHOP_MANAGEMENT,
];
const allRoles = [...allRolesExceptDriver, EnumPersonRole.DRIVER];

export type RequiredFeatures = RequiredFeature[];

export type SidebarChild = {
  name: string;
  link?: string;
  baseURL?: string;
  isactive: boolean;
  availableInLightVersion: boolean;
  restrictToRoles?: EnumPersonRole[];
  restrictToLicense?: RequiredFeatures;
  restrictByFunction?: ({
    person,
  }: {
    person?: {
      isTimeTracker?: boolean | null;
      isTimeTrackerAdmin?: boolean | null;
      hideKPIs?: boolean | null;
    } | null;
  }) => boolean;
  action?: () => void;
  children?: SidebarChild[];
  helpLink?: string;
};

export type SideBarNavigation = {
  name: string;
  href: string;
  baseURL: string;
  icon: IconDefinition;
  isactive: boolean;
  availableInLightVersion: boolean;
  openChildrenWithHoverOnly?: boolean;
  restrictToRoles?: EnumPersonRole[];
  action?: () => void;
  children?: SidebarChild[];
  restrictByFunction?: ({
    person,
  }: {
    person?: {
      isTimeTracker?: boolean | null;
      isTimeTrackerAdmin?: boolean | null;
      hideKPIs?: boolean | null;
    } | null;
  }) => boolean;
};

export type SideBarCategory = {
  name: string;
  children: SideBarNavigation[];
  availableInLightVersion?: boolean;
};

// careful we call everything with CTRL+SHIFT so lowercase will not work!
export const navigationShortcutList: Record<string, ((params: Record<string, unknown>) => string) | undefined> = {
  C: getSetupOverviewPath,
  J: getMyJobsPath,
  K: getKPIOverviewPath,
  R: getRunSheetsPath,
  S: getSimulationPath,
  Y: getEventTyresPath,
  W: getWeatherPath,
};

export const homeItem = {
  name: "Home",
  href: "/",
  icon: faHome,
  isactive: true,
  availableInLightVersion: true,
  baseURL: "/",
};

export const operations = (): SideBarCategory => ({
  name: "Operations",
  availableInLightVersion: true,
  children: [
    {
      name: "Protocol",
      href: getRunSheetsPath({}),
      baseURL: "/protocol",
      icon: faClipboard,
      isactive: true,
      availableInLightVersion: true,
      children: [
        {
          name: "Run Sheet",
          link: getRunSheetsPath({}),
          isactive: true,
          availableInLightVersion: true,
          restrictToRoles: allRoles,
        },
        {
          name: "Run Compare",
          link: getRunComparePath({}),
          isactive: true,
          availableInLightVersion: true,
          restrictToRoles: allRolesExceptDriver,
        },
        {
          name: "Setup Sheets",
          link: getSetupSheetsMainViewPath({}),
          isactive: true,
          availableInLightVersion: true,
          restrictToRoles: allRolesExceptDriver,
        },
        {
          name: "Dashboard",
          link: getProtocolDashboardPath({}),
          isactive: true,
          availableInLightVersion: true,
          restrictToRoles: allRoles,
        },
      ],
    },
    {
      name: "Tyres",
      href: getEventTyresPath({}),
      baseURL: "/tyres",
      icon: faCoins,
      isactive: true,
      availableInLightVersion: true,
      restrictToRoles: allRolesExceptDriver,

      children: [
        {
          name: "Tyre Sheet",
          link: getEventTyresPath({}),
          isactive: true,
          availableInLightVersion: true,
          restrictToRoles: allRolesExceptDriver,
        },
        {
          name: "Tyre Warehouse",
          link: getWarehouseTyresPath({}),
          isactive: true,
          availableInLightVersion: true,
          restrictToRoles: allRolesExceptDriver,
        },
        {
          name: "Pressure Calculation",
          link: getTyrePressureCalculationPath({}),
          isactive: true,
          availableInLightVersion: true,
          restrictToRoles: allRolesExceptDriver,
        },
      ],
    },
    {
      name: "Analysis",
      href: getSetupOverviewPath({}),
      baseURL: "/analysis",
      icon: faChartLine,
      isactive: true,
      availableInLightVersion: true,
      restrictToRoles: allRolesExceptDriver,
      children: [
        {
          name: "Car Overview",
          link: getSetupOverviewPath({}),
          isactive: true,
          availableInLightVersion: true,
          restrictToRoles: allRolesExceptDriver,
        },
        {
          name: "KPI Dashboard",
          link: getKPIOverviewPath({}),
          restrictToRoles: [
            EnumPersonRole.ADMIN,
            EnumPersonRole.MANAGEMENT,
            EnumPersonRole.CAR_ENGINEER,
            EnumPersonRole.CHIEF_ENGINEER,
            EnumPersonRole.MECHANIC,
          ],
          restrictByFunction: ({ person }) => !person?.hideKPIs,
          isactive: true,
          availableInLightVersion: false,
        },
        {
          name: "BoP Tracker",
          link: getBOPTrackerPath({}),
          isactive: true,
          availableInLightVersion: true,
        },
      ],
    },
    {
      name: "Driver",
      href: `DriverRoute`,
      baseURL: "/driver",
      icon: faRaceHelmet as IconDefinition,
      isactive: false,
      availableInLightVersion: true,

      children: [
        {
          name: "Feedback",
          link: ``,
          isactive: false,
          availableInLightVersion: true,
        },
        {
          name: "Track Walk",
          link: ``,
          isactive: false,
          availableInLightVersion: true,
        },
      ],
    },
    {
      name: "Jobs",
      href: getCarJobsPath({}),
      baseURL: "/jobs",
      icon: faTasks,
      isactive: true,
      availableInLightVersion: true,
      restrictToRoles: allRolesExceptDriver,

      children: [
        {
          name: "Operational Jobs",
          link: getCarJobsPath({}),
          isactive: true,
          availableInLightVersion: true,
          restrictToRoles: allRolesExceptDriver,
        },
        {
          name: "My Jobs",
          link: getMyJobsPath({}),
          isactive: true,
          availableInLightVersion: true,
          restrictToRoles: allRolesExceptDriver,
        },
      ],
    },
    {
      name: "Simulation",
      href: getSimulationPath({}),
      baseURL: "/simulation",
      icon: faLaptop,
      isactive: true,
      availableInLightVersion: false,
      restrictToRoles: allRolesExceptDriver,
    },
    {
      name: "Report",
      href: getProtocolReportPath({}),
      baseURL: "/report",
      icon: faBook,
      isactive: true,
      availableInLightVersion: false,
      restrictToRoles: allRolesExceptDriver,
    },
    {
      name: "Weather",
      href: getWeatherPath({}),
      baseURL: "/weather",
      icon: faCloudSun,
      isactive: true,
      availableInLightVersion: true,
      restrictToRoles: allRolesExceptDriver,
    },
  ],
});

export const aiAndMl = (): SideBarCategory => ({
  name: "AI & ML",
  availableInLightVersion: false,
  children: [
    {
      name: "Transcribble",
      href: ``,
      baseURL: "/transcribble",
      icon: faWalkieTalkie,
      isactive: false,
      availableInLightVersion: false,
      restrictToRoles: allRolesExceptDriver,
    },
    {
      name: "AI Chat",
      href: isDev() ? getChatPath({}) : "",
      baseURL: "/chat",
      icon: faComments,
      isactive: isDev(),
      availableInLightVersion: false,
      restrictToRoles: allRolesExceptDriver,
    },
  ],
});

export const manager = (): SideBarCategory => ({
  name: "Manager",
  availableInLightVersion: true,
  children: [
    {
      name: "Calendar",
      href: getCalendarPath({}),
      baseURL: "/calendar",
      icon: faCalendarAlt,
      isactive: true,
      availableInLightVersion: true,
      restrictToRoles: allRolesExceptDriver,
    },
    {
      name: "Documents",
      href: "",
      baseURL: "/documents",
      icon: faFileLines,
      isactive: false,
      availableInLightVersion: true,
      restrictToRoles: allRolesExceptDriver,
      children: [
        {
          name: "Notes",
          link: "",
          isactive: false,
          availableInLightVersion: true,
          restrictToRoles: allRolesExceptDriver,
        },
      ],
    },
    {
      name: "Issue Manager",
      baseURL: "/issue-manager",
      href: getIssueManagerPath({}),
      icon: faWarning,
      isactive: true,
      availableInLightVersion: true,
      restrictToRoles: allRolesExceptDriver,
    },
    {
      name: "Time Tracker",
      href: getTimeTrackerPath({}),
      baseURL: "/timetracker",
      icon: faCalendarCheck,
      //TODO this restrictions seems to not work, yet
      restrictByFunction: ({ person }) => Boolean(person?.isTimeTracker),
      isactive: true,
      availableInLightVersion: false,
      restrictToRoles: allRolesExceptDriver,

      children: [
        {
          name: "Tracker",
          link: getTimeTrackerPath({}),
          isactive: true,
          availableInLightVersion: false,
          restrictByFunction: ({ person }) => Boolean(person?.isTimeTracker),
        },
        {
          name: "Export",
          link: getTimeSheetExportPath({}),
          isactive: true,
          availableInLightVersion: false,
          restrictByFunction: ({ person }) => Boolean(person?.isTimeTrackerAdmin),
        },
        {
          name: "Admin",
          link: getTimeTrackerAdminPath({}),
          isactive: true,
          availableInLightVersion: false,
          restrictByFunction: ({ person }) => Boolean(person?.isTimeTrackerAdmin),
        },
      ],
    },
  ],
});

export const partsAndSpecs = (): SideBarCategory => ({
  name: "Parts & Specs",
  availableInLightVersion: false,
  children: [
    {
      name: "Parts & Assemblies",
      baseURL: "/assemblies-parts",
      href: getAssemblyPath({}),
      icon: faWrench,
      isactive: true,
      availableInLightVersion: false,
      restrictToRoles: allRolesExceptDriver,
      children: [
        {
          name: "Assemblies",
          link: getAssemblyPath({}),
          isactive: true,
          availableInLightVersion: false,
          restrictToRoles: allRolesExceptDriver,
        },
        {
          name: "Parts",
          link: getPartsPath({}),
          isactive: true,
          availableInLightVersion: false,
          restrictToRoles: allRolesExceptDriver,
        },
        {
          name: "Parts in Event",
          link: getPartsInEventsPath({}),
          isactive: true,
          availableInLightVersion: false,
          restrictToRoles: allRolesExceptDriver,
        },
      ],
    },
    {
      name: "Specifications",
      href: getSpecOverviewPath({}),
      baseURL: "/specifications",
      icon: faTruckMonster,
      isactive: true,
      availableInLightVersion: false,
      restrictToRoles: allRolesExceptDriver,
    },
  ],
});

const allowedSsoServers = ["localhost", "dev", "rfk"];
const ssoEnabled = allowedSsoServers.some(s => config.get("api.address").includes(s));
export const configuration: () => SideBarCategory = () => {
  const conf: SideBarCategory = {
    name: "Configuration",
    availableInLightVersion: true,
    children: [
      {
        name: "Admin",
        href: getPersonsPath({}),
        baseURL: "/admin",
        icon: faSlidersH,
        restrictToRoles: [
          EnumPersonRole.ADMIN,
          EnumPersonRole.CAR_ENGINEER,
          EnumPersonRole.CHIEF_ENGINEER,
          EnumPersonRole.MANAGEMENT,
        ],
        isactive: true,
        availableInLightVersion: true,
        children: [
          {
            name: "Chassis",
            link: getChassisPath({}),
            isactive: true,
            availableInLightVersion: true,
            restrictToRoles: [
              EnumPersonRole.ADMIN,
              EnumPersonRole.CAR_ENGINEER,
              EnumPersonRole.SETUP_REVIEWER,
              EnumPersonRole.MANAGEMENT,
            ],
          },
          {
            name: "Contestants",
            link: getContestantsPath({}),
            isactive: true,
            availableInLightVersion: true,
            restrictToRoles: [EnumPersonRole.ADMIN, EnumPersonRole.MANAGEMENT],
          },
          {
            name: "Departments",
            link: getDepartmentsPath({}),
            isactive: true,
            availableInLightVersion: false,
            restrictToRoles: [EnumPersonRole.ADMIN, EnumPersonRole.MANAGEMENT],
          },
          {
            name: "Events",
            link: getRaceSeriesListPath({}),
            isactive: true,
            availableInLightVersion: true,
            restrictToRoles: [
              EnumPersonRole.ADMIN,
              EnumPersonRole.CAR_ENGINEER,
              EnumPersonRole.CHIEF_ENGINEER,
              EnumPersonRole.MANAGEMENT,
              EnumPersonRole.SETUP_REVIEWER,
            ],
          },
          {
            name: "Locations",
            link: getLocationsPath({}),
            isactive: true,
            availableInLightVersion: true,
            restrictToRoles: [EnumPersonRole.ADMIN, EnumPersonRole.MANAGEMENT],
          },
          {
            name: "Manufacturers",
            link: getManufacturersPath({}),
            isactive: true,
            availableInLightVersion: true,
            restrictToRoles: [EnumPersonRole.ADMIN, EnumPersonRole.MANAGEMENT],
          },
          {
            name: "Points Systems",
            link: getPointsSystemPath({}),
            isactive: true,
            availableInLightVersion: true,
            restrictToRoles: [EnumPersonRole.ADMIN, EnumPersonRole.MANAGEMENT],
          },
          {
            name: "Tags",
            link: getTagsPath({}),
            isactive: true,
            availableInLightVersion: true,
            restrictToRoles: [EnumPersonRole.ADMIN, EnumPersonRole.MANAGEMENT],
          },
          {
            name: "Teams",
            link: getTeamsPath({}),
            isactive: true,
            availableInLightVersion: true,
            restrictToRoles: [EnumPersonRole.ADMIN, EnumPersonRole.MANAGEMENT],
          },
          {
            name: "Tracks",
            link: getTracksPath({}),
            isactive: true,
            availableInLightVersion: true,
            restrictToRoles: [EnumPersonRole.ADMIN, EnumPersonRole.MANAGEMENT],
          },
          {
            name: "Users / Drivers",
            link: getPersonsPath({}),
            isactive: true,
            availableInLightVersion: true,
            restrictToRoles: [EnumPersonRole.ADMIN, EnumPersonRole.MANAGEMENT],
          },
          {
            name: "User Enums",
            link: getUserEnumsPath({}),
            isactive: true,
            availableInLightVersion: true,
            restrictToRoles: [EnumPersonRole.ADMIN, EnumPersonRole.MANAGEMENT],
          },
        ],
      },
      {
        name: "Templates",
        href: getCalculationsPath({}),
        baseURL: "/templates",
        icon: faObjectUngroup,
        availableInLightVersion: true,
        restrictToRoles: allRolesExceptDriver,
        isactive: true,
        children: [
          {
            name: "Assembly Templates",
            link: getAssemblyTemplatesPath({}),
            availableInLightVersion: true,
            isactive: true,
            restrictToRoles: [EnumPersonRole.ADMIN, EnumPersonRole.CAR_ENGINEER, EnumPersonRole.CHIEF_ENGINEER],
          },
          {
            name: "Calculations",
            link: getCalculationsPath({}),
            isactive: true,
            availableInLightVersion: true,
          },
          {
            name: "Job List Templates",
            link: getJobListTemplatesPath({}),
            availableInLightVersion: true,
            baseURL: "/templates",
            isactive: true,
            restrictToRoles: [
              EnumPersonRole.ADMIN,
              EnumPersonRole.CAR_ENGINEER,
              EnumPersonRole.CHIEF_ENGINEER,
              EnumPersonRole.MANAGEMENT,
              EnumPersonRole.WORKSHOP_MANAGEMENT,
            ],
          },
          {
            name: "Lap Sequences",
            link: getLapSequencesPath({}),
            availableInLightVersion: true,
            isactive: true,
            restrictToRoles: [EnumPersonRole.ADMIN, EnumPersonRole.CAR_ENGINEER, EnumPersonRole.CHIEF_ENGINEER],
          },
          {
            name: "Packages",
            link: getPackagesPath({}),
            availableInLightVersion: true,
            isactive: true,
            restrictToRoles: [
              EnumPersonRole.ADMIN,
              EnumPersonRole.CAR_ENGINEER,
              EnumPersonRole.CHIEF_ENGINEER,
              EnumPersonRole.MANAGEMENT,
              EnumPersonRole.WORKSHOP_MANAGEMENT,
            ],
          },
          {
            name: "Run Sheet Layouts",
            link: getRunSheetStylesPath({}),
            availableInLightVersion: true,
            restrictToRoles: [EnumPersonRole.ADMIN, EnumPersonRole.CHIEF_ENGINEER],
            isactive: true,
          },
          {
            name: "Setup Sheet Layouts",
            link: getSetupSheetStylesPath({}),
            isactive: true,
            availableInLightVersion: true,
          },
          {
            name: "Session Debrief ",
            link: getSessionDebriefPath({}),
            isactive: true,
            availableInLightVersion: true,
          },
        ],
      },
      {
        name: "System",
        href: getCarDataServicePath({}),
        baseURL: "/system",
        isactive: true,
        availableInLightVersion: true,
        restrictToRoles: allRolesExceptDriver,
        icon: faNetworkWired,
        children: [
          {
            name: "Agents",
            link: getCarDataServicePath({}),
            availableInLightVersion: true,
            isactive: true,
            children: [
              {
                name: "Car Data Service",
                link: getCarDataServicePath({}),
                availableInLightVersion: true,
                restrictToRoles: [EnumPersonRole.ADMIN, EnumPersonRole.CHIEF_ENGINEER],
                isactive: true,
                helpLink: "https://support.paceteq.com/oneracehub/car-data-service-configuration",
              },
              {
                name: "Timing Service",
                link: getMultipleEventsControllerPath({}),
                isactive: true,
                availableInLightVersion: true,
              },
              {
                name: "Timing Weather Service",
                link: getTimingWeatherServicePath({}),
                isactive: true,
                availableInLightVersion: true,
              },
              {
                name: "Weather Station Service",
                link: getWeatherStationPath({}),
                isactive: true,
                availableInLightVersion: false,
              },
            ],
          },
          {
            name: "Server / Data",
            link: getDataOverviewPath({}),
            isactive: true,
            availableInLightVersion: true,
            restrictToRoles: [EnumPersonRole.ADMIN, EnumPersonRole.CHIEF_ENGINEER],
            children: [
              {
                name: "Database",
                link: getDataOverviewPath({}),
                isactive: true,
                availableInLightVersion: true,
              },
              {
                name: "Offline Status",
                link: getOfflineStatusPath({}),
                isactive: true,
                availableInLightVersion: true,
              },
              ...(ssoEnabled
                ? [
                    {
                      name: "SSO",
                      link: getSsoProviderPath({}),
                      isactive: true,
                      availableInLightVersion: false,
                    },
                  ]
                : []),
            ],
          },
          {
            name: "Server Settings",
            link: getServerSettingsPath({}),
            isactive: true,
            availableInLightVersion: true,
          },
        ],
      },
    ],
  };
  return conf;
};

export const useProfile = (): SideBarCategory => {
  const auth = useAuth();
  const { toggleTheme } = useThemeContext();
  const personID = getUserID();
  const { isLoading, data } = useGetPersonInfoForProfilePageQuery({ id: personID }, { enabled: !!personID });
  return {
    name: "Profile/ System",
    children: [
      {
        name: `${isLoading ? "Account" : data?.person?.username}`,
        icon: faUser,
        href: getProfilePath({}),
        baseURL: "/profile",
        isactive: true,
        openChildrenWithHoverOnly: true,
        availableInLightVersion: true,
        children: [
          {
            name: "Profile",
            link: getProfilePath({}),
            isactive: true,
            availableInLightVersion: true,
          },
          {
            name: "Logout",
            isactive: true,
            action: () => {
              resetConfigValues();
              auth.signout();
            },
            availableInLightVersion: true,
          },
          {
            name: "Clear Cache / Reload",
            action: async () => {
              urqlReinitialize();
              await queryClient.invalidateQueries();
              clearOneRacehubDb();
              window.location.reload();
            },
            isactive: true,
            availableInLightVersion: true,
          },
          {
            name: "Switch Light/Dark",
            isactive: true,
            availableInLightVersion: true,
            action: () => toggleTheme(),
          },
          {
            name: "Export Debug File",
            isactive: true,
            availableInLightVersion: true,
            action: () => exportDbToJson(),
          },
        ],
      },
    ],
  };
};

export const useAvailableMenuChildren = (children: SidebarChild[] | undefined) => {
  const { restrictions, isLoading } = useAuth();

  if (isLoading) return [];

  const hasRequiredLicenseForMenuEntry = (child: SidebarChild) =>
    !child.restrictToLicense ||
    child.restrictToLicense.every(requiredFeature => !!restrictions.featureLicenses?.[requiredFeature]);

  const hasRequiredRoleForMenuEntry = (child: SidebarChild) => hasRequiredRoles(child.restrictToRoles);

  const sufficesAdvancedRestrictions = (child: SidebarChild) =>
    !child.restrictByFunction || child.restrictByFunction({ person: restrictions.person });

  const availableChildren = (children ?? [])
    .filter(hasRequiredLicenseForMenuEntry)
    .filter(hasRequiredRoleForMenuEntry)
    .filter(sufficesAdvancedRestrictions);

  return availableChildren;
};
